/*
  (c) 2025 Fluxonix Inc.
*/

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { jwtDecode } from "jwt-decode";

const AuthContext = createContext(null);
function isTokenValid(token) {
  try {
    const { exp } = jwtDecode(token);
    return exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

function getValidToken() {
  const lpoToken = localStorage.getItem("lpo_token");
  const userToken = localStorage.getItem("fluxonix_token");

  const lpoValid = lpoToken && isTokenValid(lpoToken);
  const userValid = userToken && isTokenValid(userToken);

  if (lpoValid) {
    return { token: lpoToken, userType: "lpo" };
  }

  if (userValid) {
    return { token: userToken, userType: "user" };
  }

  return null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // 🆕 Track loading status

  // 🔄 Try to restore user from localStorage on load
  useEffect(() => {
    const storedUser = localStorage.getItem("fluxonix_user");
    const valid = getValidToken();
    //const storedToken = localStorage.getItem("fluxonix_token");

    if (storedUser && valid) {
      try {
        const parsed = JSON.parse(storedUser);
        parsed.userType = valid.userType;
        setUser(parsed);
        setToken(valid.token);
        setIsAuthenticated(true);
      } catch (err) {
        console.warn("⚠️ Failed to parse stored user:", err);
        localStorage.removeItem("fluxonix_user");
        localStorage.removeItem("fluxonix_token");
      }
    }

    setIsLoading(false);
  }, []);

  // 🧠 Sync logout events across browser tabs
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === "fluxonix_logout" && event.newValue === "true") {
        console.log("🔄 Detected logout in another tab, clearing auth context.");
        performLogout(false);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // 🧼 Safe logout that clears everything, including MSAL cache
  const performLogout = (broadcast = true) => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);

    localStorage.removeItem("fluxonix_user");
    localStorage.removeItem("fluxonix_token");
    localStorage.removeItem("lpo_token");
    sessionStorage.removeItem("fluxonix_last_email");

    // Clear MSAL cache if it exists
    if (window._fluxonix_msal) {
      try {
        window._fluxonix_msal.logoutRedirect({ postLogoutRedirectUri: "/" });
      } catch (err) {
        console.warn("⚠️ MSAL logout redirect failed:", err);
      }
    }

    // Broadcast logout event to other tabs
    if (broadcast) {
      localStorage.setItem("fluxonix_logout", "true");
      setTimeout(() => localStorage.removeItem("fluxonix_logout"), 1000);
    }
  };

  const login = useCallback((userData, jwt) => {
    if (!jwt || typeof jwt !== "string" || jwt.split(".").length !== 3) {
      console.error("❌ Invalid JWT passed to login():", jwt);
      return;
    }

    let decoded = {};
    try {
      decoded = jwtDecode(jwt);
    } catch (e) {
      console.error("❌ Failed to decode JWT in login:", e);
    }

    // 🔑 Single source of truth: backend-declared type, with token fallback
    const isLpo = userData.user_type === "lpo" || !!decoded.lpo_id;

    // 🎯 Map b2c_user → ["admin", "field_agent"]
    let derivedRoles = [];
    if (userData.role === "b2c_user") {
      derivedRoles = ["admin", "field_agent"];
    } else if (Array.isArray(userData.role)) {
      derivedRoles = userData.role;
    } else if (userData.role) {
      derivedRoles = [userData.role];
    }
    
  const normalized = {
    ...userData,
    lpo_id: userData.lpo_id ?? decoded.lpo_id ?? null,
    user_id: userData.user_id ?? null,       // for non-LPO flows if you add it later
    userType: isLpo ? "lpo" : "user",
    identityKey: isLpo ? "lpo_id" : "user_id",
    identityValue: isLpo ? decoded.lpo_id : decoded.user_id,
    roles: derivedRoles,
    permissions: Array.isArray(userData.permissions)
      ? userData.permissions
      : userData.permissions
      ? [userData.permissions]
      : [],
  };

    console.log("🔐 Normalized user payload on login:", normalized);
    setUser(normalized);
    setToken(jwt);
    setIsAuthenticated(true);

    localStorage.setItem("fluxonix_user", JSON.stringify(normalized));
    if (isLpo) {
      localStorage.setItem("lpo_token", jwt);
      localStorage.removeItem("fluxonix_token");
    } else {
      localStorage.setItem("fluxonix_token", jwt);
      localStorage.removeItem("lpo_token");
    }
  }, []);

  const logout = () => performLogout(true);

  // ✅ NEW: Role/Permission Guards
  const hasRole = (role) => user?.roles?.includes(role);
  console.log ("Checking user role:", user?.roles);
  
  const hasAnyRole = (roles = []) => {
    const match = roles.some((r) => user?.roles?.includes(r));
    console.log("🔍 Checking roles:", roles, "against", user?.roles, "=>", match);
    return match;
  };

  const hasPermission = (perm) => user?.permissions?.includes(perm);
  const hasAnyPermission = (perms = []) =>
    perms.some((p) => user?.permissions?.includes(p));

  return (
    <AuthContext.Provider
      value={{
        email: user?.email,
        user,
        token,
        isAuthenticated,
        isLoading,
        roles: user?.roles || [],
        permissions: user?.permissions || [],
        hasRole,
        hasAnyRole,
        hasPermission,
        hasAnyPermission,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}